import React from 'react'
import { useParams } from 'react-router-dom'
import { Grid } from '@which/seatbelt'

import type { BrandCampaignVerticalQuery } from '../../generated/frontend'
import { useBrandCampaignVerticalQuery } from '../../generated/frontend'
import type { PageParams } from '../../routes'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import { BasicHeader } from '../../shared/components/BasicHeader'
import { Breadcrumb } from '../../shared/components/Breadcrumb/Breadcrumb'
import { ErrorComponent } from '../../shared/components/Error/ErrorComponent'
import { PageError } from '../../shared/components/Error/PageError'
import { Loader } from '../../shared/components/Loader/Loader'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import type { PageInfo } from '../../shared/types/PageInfo'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { removeUrlQueryStringAndFragment } from '../../shared/utils/remove-url-query-string-and-fragment'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { BrandCampaignSections } from './components/BrandCampaignSections/BrandCampaignSections'

const BrandCampaignPage: React.FC = () => {
  const { verticalSlug } = useParams<PageParams>()
  const { loading, error, data } = useBrandCampaignVerticalQuery({
    variables: { verticalSlug },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })
  const { getBaseMetaTags } = useBaseMetaTags()

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.verticalPage) {
    return <PageError pageName="Brand Campaign Vertical Page" />
  }

  const {
    meta,
    brandCampaignContent: { contentItems, intro },
  } = data.verticalPage

  const baseMetaTags = getBaseMetaTags({ meta })
  const metaTags = getMetaTags({
    meta,
    baseMetaTags,
    verticalSlug,
    urls: { currentUrl: getFullUrl(), previousUrl },
  })

  return (
    <div data-testid="template-portal" data-template-type="campaigns-vertical">
      <Breadcrumb
        heading={{ text: meta.title ?? 'The Power in You' }}
        currentPage={{ text: meta.title ?? 'The Power in You', href: '' }}
        links={[]}
      />
      <PageWrapperWithMeta metaTags={metaTags}>
        <Grid>
          <BasicHeader
            header={intro.title ?? 'The Power in You'}
            standfirst={intro.standfirst ?? ''}
          />
          <BrandCampaignSections contentItems={contentItems} />
        </Grid>
      </PageWrapperWithMeta>
    </div>
  )
}

export default BrandCampaignPage

export type BrandCampaignVerticalData = BrandCampaignVerticalQuery['verticalPage'] & {
  __typename: 'VerticalPage'
}

const getMetaTags = ({
  meta,
  baseMetaTags,
  verticalSlug,
  urls,
}: {
  meta: BrandCampaignVerticalData['meta']
  baseMetaTags: JSX.Element[]
  verticalSlug: string
  urls: { currentUrl: string; previousUrl?: string }
}) => {
  const pageInfo: PageInfo = {
    wcdPageUrl: removeUrlQueryStringAndFragment(urls.currentUrl),
    previousWcdPageUrl: urls.previousUrl || '',
    vertical: verticalSlug,
    content_type: 'vertical top',
  }

  // data layer
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(pageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, pageInfo)

  return baseMetaTags.concat(dataLayer, dfpMeta)
}
